import {useQuery, UseQueryOptions} from '@tanstack/react-query';
import {type} from 'arktype';
import {AccountId} from '~/entities/operations-fiat';
import {expectEither, HttpClient, POST, ServerValidationResult, useHttpClient} from '~/library/network-facade';
import {Outcome} from '~/library/types';
import {useDebounce} from '~/library/ui-kit';

export const SwiftBankInfoResponse = type({
    bic: 'string',
    name: 'string',
});
export type SwiftBankInfoResponse = typeof SwiftBankInfoResponse.infer;

const SwiftBankInfoFields = type({
    bic: 'string',
});
type SwiftBankInfoFields = typeof SwiftBankInfoFields.infer;

function fetchSwiftBankInfo(http: HttpClient, accountId: AccountId, bic: string) {
    return http(`v1/accounts/${accountId}/send/banks/swift/search-bic`, POST({bic})).then(
        ...expectEither({
            200: SwiftBankInfoResponse,
            204: null,
            400: ServerValidationResult.to(SwiftBankInfoFields),
        })
    );
}

function useSwiftBankInfoOptions(
    accountId: AccountId,
    iban: string
): UseQueryOptions<Outcome<SwiftBankInfoResponse | null, SwiftBankInfoFields>> {
    const http = useHttpClient();

    return {
        queryKey: ['v1/accounts/{accountId}/send/banks/swift/search-bic', iban],
        queryFn: async ({client, queryKey}) => {
            const res = await fetchSwiftBankInfo(http, accountId, iban);

            switch (res.status) {
                case 200:
                    return {value: res.json};
                case 204: {
                    return {value: null};
                }
                case 400:
                    return {problem: res.json};
                case 'Abort': {
                    client.cancelQueries({queryKey});
                    throw res.reason;
                }
            }
        },
    };
}

export function useSwiftBankInfo(accountId: AccountId, bic: string) {
    const debouncedAccountId = useDebounce(200, accountId);
    const debouncedBic = useDebounce(200, bic);
    const queryOptions = useSwiftBankInfoOptions(debouncedAccountId.value, debouncedBic.value);
    const query = useQuery(queryOptions);
    return {...query, isDebouncing: debouncedAccountId.isDebouncing || debouncedBic.isDebouncing};
}
